import { router, useLocalSearchParams } from 'expo-router';
// `Check` and `Trophy` were used by the sport-input section, which is
// commented out below as part of the May-5 onboarding rebuild. Imports
// stay so re-enabling the section needs no extra changes. (GraduationCap
// went with the school block — SchoolPicker owns that icon now.)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Check, Trophy } from 'lucide-react-native';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import {
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { BrandTile } from '@/components/BrandTile';
import { Button } from '@/components/Button';
import { Heading } from '@/components/Heading';
import { Input } from '@/components/Input';
import { SchoolPicker } from '@/components/SchoolPicker';
import { colors, fonts, fontSizes, spacing } from '@/constants/theme';
import { isValidEmail } from '@/lib/contact';
import { safeInternalPath } from '@/lib/safeInternalPath';
import { signOutThisDevice } from '@/lib/signOut';
import { isApiError } from '@/services/errors';
import { onboardingStore } from '@/stores/onboarding.store';
// `Sport` typed-only import — used by the commented-out sport block; keep
// alongside the eslint-disable for symmetry.
 
import type { Sport } from '@/types/user';

const OnboardingScreen = observer(() => {
  // Deep link carried through login → verify → tabs gate; consumed here
  // so brand-new users land where they were originally headed.
  const { redirect } = useLocalSearchParams<{ redirect?: string }>();
  const [firstName, setFirstName] = useState(onboardingStore.firstName);
  const [lastName, setLastName] = useState(onboardingStore.lastName);
  const [email, setEmail] = useState(onboardingStore.email);
  const [emailFocused, setEmailFocused] = useState(false);
  // Sport state retained for the commented-out section below — keep so
  // re-enabling the input doesn't need re-wiring.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sportText, setSportText] = useState(onboardingStore.sport ?? '');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sportFocused, setSportFocused] = useState(false);
  const [firstFocused, setFirstFocused] = useState(false);
  const [lastFocused, setLastFocused] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  const { selectedSchool, submitting } = onboardingStore;
  // Sport-related store fields kept for the commented-out section.
   
  const { sport, sports, otherSport } = onboardingStore;

  useEffect(() => {
    onboardingStore.loadSports();
  }, []);

  // Sports list kept loaded so re-enabling the input section is a one-line change.
   
  const allSports = [...sports, otherSport];

  // May-5: school is optional, sport is hidden, email is required.
  // First/last name still required — same gate as before for the name half.
  const isComplete =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    isValidEmail(email);


  // ── Sport-input helpers (commented-out section below) ────────────────
  // const filteredSports = sportText.trim()
  //   ? allSports.filter((s) => s.toLowerCase().includes(sportText.toLowerCase()))
  //   : allSports;
  // const showSportResults =
  //   sportFocused && sportText.trim().length >= 1 && sport !== sportText;

  // Sport handlers kept — paired with the commented-out sport input below.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function handleSportChange(text: string) {
    setSportText(text);
    const exact = allSports.find((s) => s.toLowerCase() === text.trim().toLowerCase());
    if (exact) {
      onboardingStore.setSport(exact);
    } else if (sport) {
      onboardingStore.setSport(null);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function pickSport(s: Sport) {
    onboardingStore.setSport(s);
    setSportText(s);
    setSportFocused(false);
    Keyboard.dismiss();
  }

  async function handleFinish() {
    if (!isComplete || submitting) return;
    setErrorMessage(null);
    onboardingStore.setName(firstName.trim(), lastName.trim());
    onboardingStore.setEmail(email.trim());
    try {
      await onboardingStore.submit();
      onboardingStore.reset();
      // Land directly on the destination (home, or a referred user's deal).
      // Push priming is no longer part of signup — it prompts once on first
      // view of the home tab (app/(tabs)/index.tsx), so it can never sit
      // between finishing signup and reaching a deal.
      router.replace(safeInternalPath(redirect));
    } catch (err) {
      if (isApiError(err)) setErrorMessage(err.message);
      else setErrorMessage('Could not save your profile. Please try again.');
    }
  }

  // Escape hatch. This screen is reached with a live session, so it can be
  // showing a profile form for an account that isn't the user's (a friend's
  // never-finished signup left in the Keychain — see lib/signOut.ts). Signing
  // out clears it; AuthGate then redirects to login. Re-presses are blocked
  // by the Button's loading state.
  async function handleSignIn() {
    setSigningOut(true);
    setErrorMessage(null);
    try {
      onboardingStore.reset();
      await signOutThisDevice();
    } catch {
      // logout() swallows the network call; only a local Keychain failure
      // lands here, and the session is still in place.
      setErrorMessage('Could not sign out. Please try again.');
      setSigningOut(false);
    }
  }

  return (
    <SafeAreaView style={styles.safe} edges={['top', 'bottom']}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        style={styles.flex}
      >
        <ScrollView
          contentContainerStyle={styles.scroll}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.header}>
            <BrandTile size={56} style={styles.brandTile} />
            <Heading size="4xl">Your profile</Heading>
            <Text style={styles.subheading}>Takes about 30 seconds.</Text>
          </View>

          <View style={styles.fields}>
            <Input
              label="First name"
              focused={firstFocused}
              value={firstName}
              onChangeText={setFirstName}
              onFocus={() => setFirstFocused(true)}
              onBlur={() => setFirstFocused(false)}
              placeholder="Joe"
              autoCapitalize="words"
              autoComplete="given-name"
              textContentType="givenName"
              returnKeyType="next"
            />

            <Input
              label="Last name"
              focused={lastFocused}
              value={lastName}
              onChangeText={setLastName}
              onFocus={() => setLastFocused(true)}
              onBlur={() => setLastFocused(false)}
              placeholder="Smith"
              autoCapitalize="words"
              autoComplete="family-name"
              textContentType="familyName"
              returnKeyType="next"
            />

            <Input
              label="Email"
              focused={emailFocused}
              value={email}
              onChangeText={(value) => {
                setEmail(value);
                // Mirror to the store on every keystroke so a `submit()`
                // retried outside `handleFinish` (network failure replay,
                // double-tap race) still ships the email — otherwise the
                // backend's `needs_onboarding` gate stays stuck on
                // `email.blank?` and the user loops back here.
                onboardingStore.setEmail(value);
              }}
              onFocus={() => setEmailFocused(true)}
              onBlur={() => setEmailFocused(false)}
              placeholder="you@school.edu"
              keyboardType="email-address"
              autoComplete="email"
              textContentType="emailAddress"
              autoCapitalize="none"
              autoCorrect={false}
              returnKeyType="next"
            />

            {/* Optional as of May-5. Same picker as the campaign form. */}
            <SchoolPicker
              label="School (optional)"
              value={selectedSchool}
              onSelect={onboardingStore.selectSchool}
            />

            {/* Sport input — commented out May 5. Sport column on User
                stays; we may bring this back later. To re-enable: drop
                this comment block and the eslint-disables on
                `Sport`/`Trophy`/`Check`/`sportText`/`sportFocused`/etc.
            <View>
              <Input
                label="Sport"
                icon={Trophy}
                focused={sportFocused}
                value={sportText}
                onChangeText={handleSportChange}
                onFocus={() => setSportFocused(true)}
                onBlur={() => setTimeout(() => setSportFocused(false), 150)}
                placeholder="Pick or type your sport"
                autoCapitalize="words"
                autoCorrect={false}
                autoComplete="off"
                spellCheck={false}
                returnKeyType="done"
              />
              {showSportResults && (
                <View style={styles.dropdown}>
                  {filteredSports.length === 0 ? (
                    <Text style={styles.dropdownEmpty}>No matching sport.</Text>
                  ) : (
                    <ScrollView
                      keyboardShouldPersistTaps="handled"
                      nestedScrollEnabled
                    >
                      {filteredSports.map((s) => {
                        const selected = sport === s;
                        return (
                          <Pressable
                            key={s}
                            style={({ pressed }) => [
                              styles.dropdownRow,
                              pressed && styles.dropdownRowPressed,
                            ]}
                            onPress={() => pickSport(s)}
                            accessibilityRole="button"
                            accessibilityLabel={`Select ${s}`}
                            accessibilityState={{ selected }}
                          >
                            <Text style={styles.dropdownName}>{s}</Text>
                            {selected && <Check color={colors.primary} size={18} />}
                          </Pressable>
                        );
                      })}
                    </ScrollView>
                  )}
                </View>
              )}
            </View>
            */}
          </View>

          {errorMessage && <Text style={styles.error}>{errorMessage}</Text>}

          <Button
            label="Complete"
            variant="primary"
            fullWidth
            disabled={!isComplete}
            loading={submitting}
            onPress={handleFinish}
          />

          <View style={styles.signInRow}>
            <Text style={styles.signInPrompt}>Already have an account?</Text>
            <Button
              label="Sign in"
              variant="ghost"
              size="sm"
              loading={signingOut}
              onPress={() => void handleSignIn()}
              testID="onboarding-sign-in-btn"
            />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
});

export default OnboardingScreen;

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: colors.background },
  flex: { flex: 1 },
  scroll: { padding: spacing.xl, paddingBottom: spacing['2xl'], gap: spacing.xl },
  header: { gap: spacing.xs, alignItems: 'flex-start' },
  brandTile: { marginBottom: spacing.lg },
  subheading: { fontFamily: fonts.body, fontSize: fontSizes.base, color: colors.textMuted },
  // The school dropdown is absolutely positioned and can extend past this
  // container. Its own zIndex only orders it within `fields`, so without a
  // zIndex here the Complete button — a later sibling — paints over the
  // list and swallows taps meant for a school row.
  fields: { gap: spacing.lg, zIndex: 1 },
  error: {
    fontFamily: fonts.bodyMedium,
    fontSize: fontSizes.sm,
    color: colors.danger,
    textAlign: 'center',
  },
  // Same row as SignupCard's "Already have an account? Sign in".
  signInRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
  },
  signInPrompt: {
    fontFamily: fonts.body,
    fontSize: fontSizes.sm,
    color: colors.textMuted,
  },
});
